import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Divider,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircleIcon from '@mui/icons-material/Circle';

const faqs = [
  {
    question: "What is Centrum Fuel Benefits?",
    answer:
      "Centrum Fuel Benefits is a rewards program by Rael Kitz Corporation (RKC) where you earn points on fuel purchases at participating Centrum Fuel stations. Points can be redeemed for discounts and other benefits.",
  },
  {
    question: "What are the benefits of the app?",
    answer:
      "The app allows you to check your points balance, redeem rewards, locate participating stations, and receive exclusive offers.",
  },
  {
    question: "Who can join the Centrum Fuel Benefits program?",
    answer: (
      <Box>
        <Typography sx={{ fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6, mb: 2 }}>
          Anyone who uses Centrum Fuel and can provide a valid government ID may join.
          Both existing physical card holders and new customers are welcome.
        </Typography>

        <ul style={{ paddingLeft: "15px", marginTop: 8, marginBottom: 8, listStyle: "none" }}>
          {[
            { text: <><strong>Download the Centrum Fuel Benefits App</strong> and tap <strong>Register</strong>.</> },
            { text: "Complete the registration form and upload a selfie and a photo of your ID." },
            { text: "Complete physical verification at any participating Centrum Fuel station — a Professional Forecourt Attendant (PFA) or station manager will confirm your identity using your uploaded images." }
          ].map((item, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px" }}>
              <CircleIcon sx={{ fontSize: "0.75rem", mt: "0.6rem", flexShrink: 0 }} />
              <span style={{ marginLeft: "10px", fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6 }}>
                {item.text}
              </span>
            </li>
          ))}
        </ul>

        <Typography sx={{ fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6, mt: 2 }}>
          Open to all Centrum Fuel customers. You’ll need a valid government ID for verification.
        </Typography>
      </Box>
    ),
  },
  {
    question: "If I don’t have a physical benefits card yet, how can I create an account in the app?",
    answer: (
      <Box>
        <ul style={{ paddingLeft: "15px", listStyle: "none" }}>
          {[
            { 
              num: "1.", 
              text: <>Open the <strong>Centrum Fuel Rewards App</strong>. On the login screen, tap <strong>Sign Up</strong> (this option is for new customers).</> 
            },
            { 
              num: "2.", 
              text: (
                <>
                  Enter the following details:
                  <ul style={{ paddingLeft: "25px", marginTop: "8px", listStyleType: "disc" }}>
                    <li>First Name</li>
                    <li>Middle Name (optional)</li>
                    <li>Last Name</li>
                    <li>Email Address</li>
                    <li>Password → Confirm Password</li>
                  </ul>
                </>
              ) 
            },
            { 
              num: "3.", 
              text: "Accept the Terms and Conditions and Privacy Policy, then submit your registration." 
            },
            { 
              num: "4.", 
              text: (
                <>
                  Complete the Redemption Verification process by providing the following:
                  <ul style={{ paddingLeft: "25px", marginTop: "8px", listStyleType: "disc" }}>
                    <li>Region / Province / City or Municipality / Barangay</li>
                    <li>Address</li>
                    <li>Phone Number</li>
                    <li>Birthdate</li>
                    <li>Valid ID Number</li>
                    <li>Selfie</li>
                    <li>Selfie with Valid ID</li>
                  </ul>
                </>
              ) 
            },
            { 
              num: "5.", 
              text: "Once submitted and verified, your account will be activated. You can then start using QR Code features within the app." 
            }
          ].map((item, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px" }}>
              <span style={{ fontSize: "1.45rem", minWidth: "25px", flexShrink: 0 }}>{item.num}</span>
              {/* Changed span to div here so the inner <ul> elements render valid HTML */}
              <div style={{ fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6, marginLeft: "10px" }}>
                {item.text}
              </div>
            </li>
          ))}
        </ul>
      </Box>
    ),
  },
  {
    question: "How can I earn and redeem points?",
    answer: (
      <ul style={{ paddingLeft: "15px", listStyle: "none" }}>
        <li style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px" }}>
          <CircleIcon sx={{ fontSize: "0.75rem", mt: "0.6rem", flexShrink: 0 }} />
          <span style={{ marginLeft: "10px", fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6 }}>
            <strong>Earn Points:</strong> Gasoline – 1 liter = 2 points = ₱0.05; Diesel – 1 liter = 1 point = ₱0.25.
          </span>
        </li>
        <li style={{ display: "flex", alignItems: "flex-start" }}>
          <CircleIcon sx={{ fontSize: "0.75rem", mt: "0.6rem", flexShrink: 0 }} />
          <span style={{ marginLeft: "10px", fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6 }}>
            <strong>Redeem Points:</strong> Minimum 1,000 points; maximum 30,000 points per day. 
            Points are printed on your POS receipt and can be redeemed within 60 days after card expiry.
          </span>
        </li>
      </ul>
    ),
  },
  {
    question: "Can I convert my points into cash?",
    answer: "No. Points have no cash value and can only be redeemed for eligible rewards.",
  },
  {
    question: "Where is the Centrum Fuel Benefits program available?",
    answer: "At participating Centrum Fuel stations listed in the app.",
  },
  {
    question: "I have lost access to my phone number and email. What should I do?",
    answer: "Contact the Centrum Fuel Benefits customer support team or visit a participating station to verify your identity and update your contact details.",
  },
];

const FAQSection = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Container maxWidth="lg">
      <Typography 
        variant="h3" 
        fontWeight="bold" 
        gutterBottom 
        sx={{ mt: { lg: 15, md: 20, sm: 5, xs: 5 }, mb: 3 }}
      >
        Frequently Asked Questions
      </Typography>

      {faqs.map((faq, index) => (
        <React.Fragment key={index}>
          <Accordion
            disableGutters
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
            sx={{ 
              boxShadow: 'none', 
              backgroundColor: 'transparent',
              '&:before': { display: 'none' } 
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon sx={{ fontSize: '2rem' }} />}
              sx={{
                px: 0, 
                '& .MuiAccordionSummary-content': {
                  margin: '20px 0', 
                },
                '&.Mui-expanded': {
                  minHeight: '48px', 
                }
              }}
            >
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 600 }}>
                {faq.question}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pb: 3 }}>
              {typeof faq.answer === "string" ? (
                <Typography sx={{ fontSize: '1.7rem', fontWeight: 450, lineHeight: 1.6 }}>
                  {faq.answer}
                </Typography>
              ) : (
                faq.answer
              )}
            </AccordionDetails>
          </Accordion>

          {index < faqs.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Container>
  );
};

export default FAQSection;